import { TReferenceTableTab, TReferenceTableTabGroup } from './types';

export const DEFAULT_TAB = 'additional-services';

/** Tabs rendered directly in the tab bar (not collapsed into a group). */
export const PLAIN_TABS: TReferenceTableTab[] = [
	{ value: DEFAULT_TAB, label: 'Additional Services' },
	{ value: 'general-contract-documents', label: 'General Contract Documents' },
	{ value: 'email-templates', label: 'Email Template' },
	{ value: 'shopify-sales-channels', label: 'Shopify Sales Channels' },
	{ value: 'eas-project-settings', label: 'EAS Project Settings' },
];

/** Cross-cutting dictionaries that are not tied to one business area. */
export const GENERAL_TAB_GROUP: TReferenceTableTabGroup = {
	name: 'General',
	items: [
		{ value: 'countries', label: 'Countries' },
		{ value: 'currencies', label: 'Currencies' },
		{ value: 'enums', label: 'Enums' },
		{ value: 'supported-postal-operators', label: 'Supported Postal Operators' },
	],
};

/** EU DDP dictionaries. */
export const EU_DDP_TAB_GROUP: TReferenceTableTabGroup = {
	name: 'EU DDP',
	items: [
		{ value: 'euDdpDeliveryOperators', label: 'EU DDP Delivery Operators' },
		{ value: 'euDdpTariffs', label: 'EU DDP Tariffs' },
		{ value: 'euDdpFees', label: 'EU DDP Fees' },
	],
};

/** Exchange rate dictionaries. */
export enum EExchangeRatesTabGroup {
	DAILY_EXCHANGE_RATES = 'daily-exchange-rates',
	EU_MONTHLY_EXCHANGE_RATES = 'eu-monthly-exchange-rates',
	ANNUAL_EXCHANGE_RATES = 'annual-exchange-rates',
	GBP_MONTHLY_EXCHANGE_RATES = 'gbp-monthly-exchange-rates',
	UK_EXCHANGE_RATES = 'uk-exchange-rates',
}
export enum EExchangeRatesTabGroupLabel {
	DAILY_EXCHANGE_RATES = 'EU Daily Exchange Rates',
	EU_MONTHLY_EXCHANGE_RATES = 'EU Monthly Exchange Rates',
	ANNUAL_EXCHANGE_RATES = 'EU Annual Exchange Rates, EUR',
	GBP_MONTHLY_EXCHANGE_RATES = 'UK Monthly Exchange Rates',
	UK_EXCHANGE_RATES = 'UK Exchange rates',
}
export const EXCHANGE_RATES_TAB_GROUP: TReferenceTableTabGroup = {
	name: 'Exchange Rates',
	items: [
		{
			value: EExchangeRatesTabGroup.DAILY_EXCHANGE_RATES,
			label: EExchangeRatesTabGroupLabel.DAILY_EXCHANGE_RATES,
		},
		{
			value: EExchangeRatesTabGroup.EU_MONTHLY_EXCHANGE_RATES,
			label: EExchangeRatesTabGroupLabel.EU_MONTHLY_EXCHANGE_RATES,
		},
		{
			value: EExchangeRatesTabGroup.ANNUAL_EXCHANGE_RATES,
			label: EExchangeRatesTabGroupLabel.ANNUAL_EXCHANGE_RATES,
		},
		{
			value: EExchangeRatesTabGroup.GBP_MONTHLY_EXCHANGE_RATES,
			label: EExchangeRatesTabGroupLabel.GBP_MONTHLY_EXCHANGE_RATES,
		},
		{
			value: EExchangeRatesTabGroup.UK_EXCHANGE_RATES,
			label: EExchangeRatesTabGroupLabel.UK_EXCHANGE_RATES,
		},
	],
};

/** Tax and duty dictionaries. */
export enum ETaxDutiesTabGroup {
	EU_VAT_RATES = 'eu-vat-rates',
	NON_EU_VAT_RATES = 'non-eu-vat-rates',
	HS6P_CODES = 'hs6p-codes',
	EU_CUSTOMS_DUTY_RATES = 'eu-customs-duty-rates',
	EU_SPECIAL_VAT_TERRITORIES = 'eu-special-vat-territories',
}
export enum ETaxDutiesTabGroupLabel {
	EU_VAT_RATES = 'EU VAT Rates',
	NON_EU_VAT_RATES = 'NON EU VAT Rates',
	HS6P_CODES = 'HS6+ Codes',
	EU_CUSTOMS_DUTY_RATES = 'EU Customs Duty Rates',
	EU_SPECIAL_VAT_TERRITORIES = 'EU Special VAT Territories',
}
export const TAX_DUTIES_TAB_GROUP: TReferenceTableTabGroup = {
	name: 'Tax & Duties',
	items: [
		{
			value: ETaxDutiesTabGroup.EU_VAT_RATES,
			label: ETaxDutiesTabGroupLabel.EU_VAT_RATES,
		},
		{
			value: ETaxDutiesTabGroup.NON_EU_VAT_RATES,
			label: ETaxDutiesTabGroupLabel.NON_EU_VAT_RATES,
		},
		{
			value: ETaxDutiesTabGroup.HS6P_CODES,
			label: ETaxDutiesTabGroupLabel.HS6P_CODES,
		},
		{
			value: ETaxDutiesTabGroup.EU_CUSTOMS_DUTY_RATES,
			label: ETaxDutiesTabGroupLabel.EU_CUSTOMS_DUTY_RATES,
		},
		{
			value: ETaxDutiesTabGroup.EU_SPECIAL_VAT_TERRITORIES,
			label: ETaxDutiesTabGroupLabel.EU_SPECIAL_VAT_TERRITORIES,
		},
	],
};

/** Fee dictionaries served by the EasFee API. */
export const EAS_FEE_TAB_GROUP: TReferenceTableTabGroup = {
	name: 'Fees',
	items: [
		{ value: 'fee-classes', label: 'Fee Classes' },
		{ value: 'monthly-fees', label: 'Monthly Subscriptions' },
		{ value: 'annual-fee-types', label: 'Non-Union Subscriptions' },
		{ value: 'invoicing-fee-types', label: 'Invoicing Fee Types' },
	],
};

/** PPWR (Packaging and Packaging Waste Regulation) dictionaries. */
export enum EPpwrTabGroup {
	PACKAGING_TYPES = 'ppwr-packaging-types',
	EPR_SERVICE_FEES = 'ppwr-epr-service-fees',
}
export enum EPpwrTabGroupLabel {
	PACKAGING_TYPES = 'Packaging Types',
	EPR_SERVICE_FEES = 'EPR Service Fees',
}
export const PPWR_TAB_GROUP: TReferenceTableTabGroup = {
	name: 'PPWR',
	items: [
		{ value: EPpwrTabGroup.PACKAGING_TYPES, label: EPpwrTabGroupLabel.PACKAGING_TYPES },
		{ value: EPpwrTabGroup.EPR_SERVICE_FEES, label: EPpwrTabGroupLabel.EPR_SERVICE_FEES },
	],
};

/** Tabs collapsed into dropdowns, rendered after the plain tabs like the header nav groups. */
export const TAB_GROUPS: TReferenceTableTabGroup[] = [
	GENERAL_TAB_GROUP,
	EU_DDP_TAB_GROUP,
	EXCHANGE_RATES_TAB_GROUP,
	TAX_DUTIES_TAB_GROUP,
	EAS_FEE_TAB_GROUP,
	PPWR_TAB_GROUP,
];

export const GROUPED_TAB_VALUES: string[] = TAB_GROUPS.flatMap((group) =>
	group.items.map((item) => item.value),
);

/** Every tab value in display order: plain tabs first, then grouped ones. */
export const ALL_TAB_VALUES: string[] = [
	...PLAIN_TABS.map((tab) => tab.value),
	...GROUPED_TAB_VALUES,
];
